import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useState } from 'react';
import type { MetaAdSetWithInsights, MetaAdWithInsights } from 'services/api/metaInsights.api';
import { useLazyGetMetaAdsWithInsightsQuery } from 'services/api/metaInsights.api';
import { META_BLUE, NAVY, RADIUS_ITEM, STATUS_NEUTRAL, SURFACE_CONTAINER_LOW, WHATSAPP_GREEN } from 'styles/tokens';

import {
  formatAdSetDestinationType,
  formatAdSetOptimizationGoal,
  isWhatsAppDestinationType,
} from '../utils/metaTypeLabels';
import {
  type DerivedKpiInputs,
  DerivedKpis,
  formatCurrency,
  LeadCounts,
  LeadStatsStrip,
  labelSx,
  MetaInsightsKpiGrid,
  parseSpend,
  SectionHeader,
  sectionTitleSx,
  ViewLeadsLink,
} from './metaDrillDownPrimitives';

interface Props {
  adSet: MetaAdSetWithInsights;
  metaConnectionId: number;
  currency: string | null;
  since: string;
  until: string;
  businessUnitId?: number;
  selectedAdId: string | null;
  onSelectAd: (ad: MetaAdWithInsights) => void;
}

const MetaAdSetSheet: React.FC<Props> = ({
  adSet,
  metaConnectionId,
  currency,
  since,
  until,
  businessUnitId,
  selectedAdId,
  onSelectAd,
}) => {
  const [getAds] = useLazyGetMetaAdsWithInsightsQuery();
  const [ads, setAds] = useState<MetaAdWithInsights[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getAds({
      metaConnectionId,
      adSetId: adSet.id,
      since,
      until,
      ...(businessUnitId ? { businessUnitId } : {}),
      fields: ['spend', 'impressions', 'reach', 'clicks', 'cpc'],
    })
      .unwrap()
      .then((result) => {
        if (!cancelled) setAds(result.ads);
      })
      .catch(() => {
        if (!cancelled) setAds([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [metaConnectionId, adSet.id, since, until, businessUnitId, getAds]);

  const insights = adSet.insights;
  const adSetSpend = parseSpend(insights?.spend);
  const optGoalLabel = formatAdSetOptimizationGoal(adSet.optimizationGoal);
  const destinationLabel = formatAdSetDestinationType(adSet.destinationType);
  const destinationColor = isWhatsAppDestinationType(adSet.destinationType) ? WHATSAPP_GREEN : META_BLUE;
  const leads = adSet.leadsStats;

  const derivedKpiInputs: DerivedKpiInputs = {
    spend: adSetSpend,
    clicks: parseSpend(insights?.clicks),
    contactados: leads?.leadsWithClientResponses ?? null,
    inCrm: leads?.leadsUploadedToCRM ?? null,
    currency,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {(optGoalLabel || destinationLabel) && (
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {optGoalLabel && (
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.5 }}>Optimización</Typography>
              <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 600, fontSize: '0.88rem' }}>
                {optGoalLabel}
              </Typography>
            </Box>
          )}

          {destinationLabel && (
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.5 }}>Tipo de destino</Typography>
              <Chip
                label={destinationLabel}
                size="small"
                sx={{
                  bgcolor: alpha(destinationColor, 0.1),
                  color: destinationColor,
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  borderRadius: RADIUS_ITEM,
                }}
              />
            </Box>
          )}
        </Box>
      )}

      <Box>
        <SectionHeader title="Rendimiento en Meta Ads" source="meta" />
        <MetaInsightsKpiGrid insights={insights ?? null} currency={currency} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <SectionHeader title="Leads en nuestro CRM" source="crm" />
        <LeadCounts
          inputs={{
            contactados: leads?.leadsWithClientResponses ?? null,
            inCrm: leads?.leadsUploadedToCRM ?? null,
          }}
        />
        <DerivedKpis inputs={derivedKpiInputs} />
      </Box>

      <ViewLeadsLink
        field="metaAdSetExternalId"
        value={adSet.id}
        label="Ver leads de este conjunto"
        businessUnitId={businessUnitId}
        since={since}
        until={until}
      />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Typography sx={sectionTitleSx}>Anuncios</Typography>
          {loading && <CircularProgress size={14} sx={{ color: META_BLUE }} />}
        </Box>

        {!loading && (!ads || ads.length === 0) && (
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: STATUS_NEUTRAL, fontSize: '0.85rem' }}>
            Sin anuncios disponibles para este conjunto en el período.
          </Typography>
        )}

        {ads && ads.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {ads.map((ad) => {
              const isSelected = selectedAdId === ad.id;
              const adSpend = parseSpend(ad.insights?.spend);
              return (
                <Box
                  key={ad.id}
                  onClick={() => onSelectAd(ad)}
                  sx={{
                    borderRadius: RADIUS_ITEM,
                    bgcolor: isSelected ? alpha(META_BLUE, 0.08) : SURFACE_CONTAINER_LOW,
                    p: 1.5,
                    cursor: 'pointer',
                    transition: 'background-color 140ms ease',
                    '&:hover': { bgcolor: alpha(META_BLUE, 0.08) },
                    outline: isSelected ? `1px solid ${alpha(META_BLUE, 0.35)}` : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: 'Jost, sans-serif',
                        color: NAVY,
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {ad.name ?? ad.id}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.6), fontSize: '0.78rem' }}>
                      {formatCurrency(ad.insights?.spend ?? null, currency)}
                    </Typography>
                    <ChevronRightIcon sx={{ color: alpha(NAVY, 0.4), fontSize: '1.1rem' }} />
                  </Box>
                  <Box sx={{ pt: 0.75 }}>
                    <LeadStatsStrip stats={ad.leadsStats} spend={adSpend} currency={currency} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MetaAdSetSheet;
